import SpaceBetween from '@cloudscape-design/components/space-between';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useMarkAllRead, useDeleteNotification } from '../hooks/queries/useNotifications';
import { Notification } from '../api/notifications';
import { LoadingSpinner } from '../components/LoadingSpinner';

function timeAgo(createdAt: number): string {
  const seconds = Math.floor((Date.now() - createdAt) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications();
  const { mutate: markAllRead } = useMarkAllRead();
  const { mutate: deleteNotification } = useDeleteNotification();

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  const handleClick = (notification: Notification) => {
    if (notification.link) navigate(notification.link);
  };

  if (isLoading) return <LoadingSpinner message="Loading notifications..." />;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          unreadCount > 0 ? (
            <Button onClick={() => markAllRead()}>Mark all read</Button>
          ) : undefined
        }
      >
        Notifications {unreadCount > 0 && `(${unreadCount} unread)`}
      </Header>

      {notifications.length === 0 ? (
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'xxl' }}>
          <Box variant="strong">No notifications</Box>
          <Box variant="p">You're all caught up.</Box>
        </Box>
      ) : (
        <div style={{ maxWidth: '720px' }}>
          {notifications.map(notification => (
            <div
              key={notification.notificationId}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '14px 16px',
                borderBottom: '1px solid #e9ebed',
                background: notification.isRead ? 'white' : '#f0f7ff',
                borderRadius: '4px',
                marginBottom: '2px',
              }}
            >
              {/* Unread dot */}
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: notification.isRead ? 'transparent' : '#0972d3',
                flexShrink: 0,
                marginTop: '6px',
              }} />

              <div style={{ flex: 1 }}>
                {notification.link ? (
                  <Link
                    onFollow={(e) => { e.preventDefault(); handleClick(notification); }}
                  >
                    {notification.message}
                  </Link>
                ) : (
                  <Box>{notification.message}</Box>
                )}
                <Box fontSize="body-s" color="text-body-secondary">
                  {timeAgo(notification.createdAt)}
                </Box>
              </div>

              <Button
                variant="icon"
                iconName="close"
                ariaLabel="Dismiss"
                onClick={() => deleteNotification(notification.notificationId)}
              />
            </div>
          ))}
        </div>
      )}
    </SpaceBetween>
  );
}
